import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, fmtTRY } from "../lib/api";
import { ArrowRight, Wallet, CheckCircle2, ShoppingCart, Info, GitCompare } from "lucide-react";
import { itemTotal } from "./Shopping";
import { computeFullSettlement } from "../lib/settlement";

export default function OdemeDengesi() {
  const [bal, setBal] = useState(null);
  const [settle, setSettle] = useState(null);
  const [digerSummary, setDigerSummary] = useState({ target_try: 0, target_eur: 0, paid_try: 0, paid_eur: 0 });
  const [cabins, setCabins] = useState([]);
  const [payments, setPayments] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [shoppingItems, setShoppingItems] = useState([]);
  const [settings, setSettings] = useState({ eur_try_rate: 55.32, realization_shop_pct: 100, realization_diger_pct: 100 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s, d, c, p, a, sh, st] = await Promise.all([
        api.get("/muhasebe/balance"),
        api.get("/muhasebe/shopping-settlement"),
        api.get("/diger-maliyet-summary"),
        api.get("/cabins"),
        api.get("/payments"),
        api.get("/actuals"),
        api.get("/shopping"),
        api.get("/settings"),
      ]);
      setBal(b.data);
      setSettle(s.data);
      setDigerSummary(d.data || {});
      setCabins((Array.isArray(c.data) ? c.data : []).filter((x) => !x.empty));
      setPayments(Array.isArray(p.data) ? p.data : []);
      setActuals(Array.isArray(a.data) ? a.data : []);
      setShoppingItems(Array.isArray(sh.data) ? sh.data : []);
      setSettings(st.data || {});
    } catch (e) { console.error("Balance load:", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // DIGER SETTLE — Section 2 (shopping-settlement) ile birebir yapıda,
  // sadece Tekne Diğer Maliyet için. UI'da Section 3'ü Section 2 gibi göstermek için.
  const digerSettle = useMemo(() => {
    if (!cabins.length) return { total_people: 0, cabins: [], total_try: 0, per_person_share_try: 0 };
    const parsePct = (v) => {
      if (v === undefined || v === null || v === "") return 100;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 100;
    };
    const digerPct = parsePct(settings.realization_diger_pct) / 100;
    const totalKisi = cabins.reduce((s, c) => s + (c.people?.length || 0), 0);
    const total_try = (digerSummary.target_try || 0) * digerPct;
    const per_person = totalKisi > 0 ? total_try / totalKisi : 0;
    // Offset semantiği Section 2 ile aynı (backend shopping-settlement uyumlu):
    //   c1 (İsmet) alacaklı, diğerlerinin pozitif offset_try'ı borç.
    const ismetCabin = cabins.find((c) => c.id === "c1") || cabins[0];
    const ismetId = ismetCabin ? ismetCabin.id : "";
    let ismetCredit = 0;
    cabins.forEach((c) => {
      if (c.id === ismetId) return;
      const off = Number(c.offset_try || 0);
      if (off > 0) ismetCredit += off;
    });
    // User → cabin map (actuals için) + full_name → cabin map (for_all payer için)
    const u2c = {};
    const name2c = {};
    cabins.forEach((c) => {
      (c.occupants || []).forEach((u) => { u2c[u] = c.id; });
      (c.people || []).forEach((p) => {
        const fn = (p.full_name || "").trim();
        if (fn) name2c[fn] = c.id;
      });
    });
    const rows = cabins.map((c) => {
      const size = c.people?.length || 0;
      const share = per_person * size;
      // Bu kabinin diğer_maliyet ödemeleri
      let paid = 0;
      payments.forEach((p) => {
        if ((p.payment_type || "gemi") !== "diger_maliyet") return;
        const amtTry = (p.amount_try || 0) + (p.amount_eur || 0) * (Number(settings.eur_try_rate) || 55.32);
        if (p.for_all) {
          // Ödeyen belirtilmişse o kabine tam kredi. Belirsizse Section 3'te gösterme (nötr, fair'e etkisiz).
          const payerLabel = (p.person_label || "").trim();
          if (payerLabel && name2c[payerLabel] === c.id) paid += amtTry;
          return;
        }
        const payer = p.cabin_id || name2c[p.person_label || ""] || "";
        if (payer !== c.id) return;
        paid += amtTry;
      });
      // Signed offset
      let offset_adj = 0;
      if (c.id === ismetId) offset_adj = ismetCredit;
      else {
        const off = Number(c.offset_try || 0);
        if (off > 0) offset_adj = -off;
      }
      const balance = paid - share;
      return {
        cabin_id: c.id,
        name: c.name,
        size,
        share_try: share,
        paid_try: paid,
        offset_try: 0,
        balance_try: balance,
      };
    });
    return { total_people: totalKisi, cabins: rows, total_try, per_person_share_try: per_person };
  }, [cabins, payments, digerSummary, settings]);

  // FULL SETTLEMENT — Shared lib (Dashboard ile aynı hesap)
  const fullSettlement = useMemo(() => computeFullSettlement({
    cabins, payments, actuals, shoppingItems, settings, digerSummary, itemTotal,
  }), [cabins, payments, actuals, shoppingItems, settings, digerSummary]);

  if (loading) return <div className="tk-card p-6 text-center text-sm" style={{ color: "hsl(var(--text-2))" }}>Yükleniyor…</div>;
  if (!bal) return null;

  const kirlamaEur = bal.cabin_total_eur || 0;
  const digerEur = digerSummary.target_eur || 0;
  const targetEur = kirlamaEur + digerEur;
  const paidEur = (bal.collected_eur || 0) + (digerSummary.paid_eur || 0);
  const remainingEur = Math.max(0, targetEur - paidEur);
  const pct = Math.min(100, Math.round((paidEur / (targetEur || 1)) * 100));
  const fullyPaid = remainingEur <= 0.5;

  return (
    <div className="space-y-5" data-testid="odeme-dengesi-tab">
      {/* === Üst Özet Blok — Tekne Kiralama + Diğer Gider === */}
      <div className="tk-card p-5" data-testid="balance-summary-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Tekne Toplam Hedef</div>
            <div className="mt-1 text-2xl md:text-3xl font-display font-semibold">{fmtEUR(targetEur)}</div>
            <div className="text-xs mt-2 space-y-0.5">
              <div className="flex items-center justify-between gap-3">
                <span style={{ color: "hsl(var(--text-2))" }}>Tekne Kiralama:</span>
                <b className="text-[hsl(var(--text-1))]">{fmtEUR(kirlamaEur)}</b>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span style={{ color: "hsl(var(--text-2))" }}>Diğer Gider:</span>
                <b className="text-[hsl(var(--text-1))]">{fmtEUR(digerEur)}</b>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-right" style={{ color: "hsl(var(--text-2))" }}>Ödenen / Kalan</div>
            <div className="mt-1 text-right font-display font-semibold text-lg" data-testid="collected-eur">
              <span style={{ color: "hsl(var(--success))" }}>{fmtEUR(paidEur)}</span>
              <span style={{ color: "hsl(var(--text-2))" }}> / </span>
              <span style={{ color: fullyPaid ? "hsl(var(--success))" : "hsl(var(--error))" }} className="font-bold" data-testid="remaining-eur">{fullyPaid ? "Tamam" : fmtEUR(remainingEur)}</span>
            </div>
            <div className="text-xs mt-2 space-y-0.5">
              <div className="flex items-center justify-between gap-3">
                <span style={{ color: "hsl(var(--text-2))" }}>Tekne Kiralama:</span>
                <span>
                  <span style={{ color: "hsl(var(--success))" }}>{fmtEUR(bal.collected_eur || 0)}</span>
                  <span style={{ color: "hsl(var(--text-2))" }}> / </span>
                  <b style={{ color: (bal.remaining_eur || 0) <= 0.5 ? "hsl(var(--success))" : "hsl(var(--error))" }}>{fmtEUR(bal.remaining_eur || 0)}</b>
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span style={{ color: "hsl(var(--text-2))" }}>Diğer Gider:</span>
                <span>
                  <span style={{ color: "hsl(var(--success))" }}>{fmtEUR(digerSummary.paid_eur || 0)}</span>
                  <span style={{ color: "hsl(var(--text-2))" }}> / </span>
                  <b style={{ color: (digerEur - (digerSummary.paid_eur || 0)) <= 0.5 ? "hsl(var(--success))" : "hsl(var(--error))" }}>{fmtEUR(Math.max(0, digerEur - (digerSummary.paid_eur || 0)))}</b>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: fullyPaid ? "hsl(var(--success))" : "hsl(var(--primary))" }} />
        </div>
        <div className="mt-1 text-[11px] text-right" style={{ color: "hsl(var(--text-2))" }}>%{pct}</div>
      </div>

      <div>
        <h2 className="text-lg font-display font-semibold mb-2 flex items-center gap-2">
          <Wallet className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} /> 1. Tekne Ödemeleri - Kabin Dağılımı
        </h2>
        <div className="space-y-3">
          {(bal.cabins || []).map((c) => {
            const cabinDone = (c.remaining_eur || 0) <= 0.5;
            return (
              <div key={c.cabin_id} className="tk-card p-4" data-testid={`cabin-balance-${c.cabin_id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                  <div>
                    <div className="font-display font-semibold">{c.name}</div>
                    <div className="text-xs" style={{ color: "hsl(var(--text-2))" }}>Kabin Toplam: <span className="text-[hsl(var(--text-1))]">{fmtEUR(c.total_eur)}</span></div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs" style={{ color: "hsl(var(--text-2))" }}>Kalan</div>
                    <div
                      className="font-display font-bold text-lg"
                      style={{ color: cabinDone ? "hsl(var(--success))" : "hsl(var(--error))" }}
                    >
                      {cabinDone ? <span className="inline-flex items-center gap-0.5"><CheckCircle2 className="w-4 h-4" /> Tamam</span> : fmtEUR(c.remaining_eur)}
                    </div>
                    <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>
                      Ödenen: <span style={{ color: "hsl(var(--primary))" }}>{fmtEUR(c.paid_eur)}</span>
                    </div>
                  </div>
                </div>
                <div className="tk-divider pt-2 space-y-1">
                  {(c.persons || []).map((p, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center text-sm" data-testid={`person-balance-${c.cabin_id}-${idx}`}>
                      <div className="truncate">{p.full_name || "—"}</div>
                      <div className="text-right" style={{ color: "hsl(var(--text-2))" }}>Pay: <b className="text-[hsl(var(--text-1))]">{fmtEUR(p.share_eur)}</b></div>
                      <div className="text-right" style={{ color: "hsl(var(--text-2))" }}>Ödenen: <b style={{ color: "hsl(var(--primary))" }}>{fmtEUR(p.paid_eur)}</b></div>
                      <div className="text-right font-semibold" style={{ color: p.balance_eur >= -0.5 ? "hsl(var(--success))" : "hsl(var(--error))" }}>
                        {p.balance_eur >= -0.5 ? "+" : ""}{fmtEUR(p.balance_eur)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* === BÖLÜM 2: Alışveriş Netleştirme — Kabin Bazlı === */}
      {settle && settle.total_people > 0 && (
        <div>
          <h2 className="text-lg font-display font-semibold mb-2 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} /> 2. Alışveriş Ödemeleri - Kabin Dağılımı
          </h2>

          {/* Bilgi rozeti KALDIRILDI: TL düzeltme uyarı satırı — user tarafından silinmesi istendi */}

          <div className="tk-card p-4 space-y-3" data-testid="shopping-settlement-card">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[11px] uppercase tracking-widest" style={{ color: "hsl(var(--text-2))" }}>Toplam Harcama</div>
                <div className="font-display font-semibold">{fmtTRY(settle.total_try)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest" style={{ color: "hsl(var(--text-2))" }}>Kişi Sayısı</div>
                <div className="font-display font-semibold">{settle.total_people}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest" style={{ color: "hsl(var(--text-2))" }}>Kişi Başı Pay</div>
                <div className="font-display font-semibold" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(settle.per_person_share_try)}</div>
              </div>
            </div>

            {/* Kabin bazlı bakiye */}
            <div className="tk-divider pt-3">
              <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "hsl(var(--text-2))" }}>Kabin Bazlı</div>
              <div className="space-y-1">
                {(settle.cabins || []).map((c) => {
                  const shopAdj = fullSettlement.shopShareAdj?.[c.cabin_id] || 0;
                  const share = (c.share_try || 0) + shopAdj;
                  const paidT = c.paid_try || 0;
                  const bal = paidT - share;
                  return (
                  <div key={c.cabin_id} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center text-sm" data-testid={`net-cabin-${c.cabin_id}`}>
                    <div className="truncate">
                      <span className="font-medium">{c.name}</span>
                      <span className="ml-1 text-[11px]" style={{ color: "hsl(var(--text-2))" }}>· {c.size} kişi</span>
                    </div>
                    <div className="text-right text-[11px]" style={{ color: "hsl(var(--text-2))" }} title="Alışveriş payı (offset absorbe ile ayarlanmış)">Pay: <b className="text-[hsl(var(--text-1))]">{fmtTRY(share)}</b></div>
                    <div className="text-right text-[11px]" style={{ color: "hsl(var(--text-2))" }} title="Gerçek alışveriş ödemesi">Ödenen: <b className="text-[hsl(var(--text-1))]">{fmtTRY(paidT)}</b></div>
                    <div className="text-right font-semibold min-w-[90px]" style={{ color: bal >= 0 ? "hsl(var(--success))" : "hsl(var(--error))" }}>
                      {bal >= 0 ? "+" : ""}{fmtTRY(bal)}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Transfer önerileri KALDIRILDI: sadece en alttaki birleşik "Kim Kime Ne Ödemeli" bloğunda göster (3 tipi kapsayan) */}
          </div>
        </div>
      )}

      {/* === BÖLÜM 3: Tekne Diğer Giderler - Kabin Dağılımı (Section 2 ile birebir tasarım) === */}
      <div>
        <h2 className="text-lg font-display font-semibold mb-2 flex items-center gap-2">
          <Wallet className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} /> 3. Tekne Diğer Giderler - Kabin Dağılımı
        </h2>

        <div className="tk-card p-4 space-y-3" data-testid="diger-maliyet-card">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "hsl(var(--text-2))" }}>Toplam Harcama</div>
              <div className="font-display font-semibold">{fmtTRY(digerSettle.total_try)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "hsl(var(--text-2))" }}>Kişi Sayısı</div>
              <div className="font-display font-semibold">{digerSettle.total_people}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "hsl(var(--text-2))" }}>Kişi Başı Pay</div>
              <div className="font-display font-semibold" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(digerSettle.per_person_share_try)}</div>
            </div>
          </div>

          {/* Kabin bazlı bakiye — Section 2 ile birebir aynı grid */}
          <div className="tk-divider pt-3">
            <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "hsl(var(--text-2))" }}>Kabin Bazlı</div>
            <div className="space-y-1">
              {(digerSettle.cabins || []).map((c) => {
                const digerAdj = fullSettlement.digerShareAdj?.[c.cabin_id] || 0;
                const displayedShare = (c.share_try || 0) + digerAdj;
                const paidAdj = c.paid_try || 0;
                const bal = paidAdj - displayedShare;
                return (
                <div key={c.cabin_id} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center text-sm" data-testid={`diger-net-cabin-${c.cabin_id}`}>
                  <div className="truncate">
                    <span className="font-medium">{c.name}</span>
                    <span className="ml-1 text-[11px]" style={{ color: "hsl(var(--text-2))" }}>· {c.size} kişi</span>
                  </div>
                  <div className="text-right text-[11px]" style={{ color: "hsl(var(--text-2))" }} title="Tekne Diğer payı">Pay: <b className="text-[hsl(var(--text-1))]">{fmtTRY(displayedShare)}</b></div>
                  <div className="text-right text-[11px]" style={{ color: "hsl(var(--text-2))" }} title="Tekne Diğer'e ödediği">Ödenen: <b className="text-[hsl(var(--text-1))]">{fmtTRY(paidAdj)}</b></div>
                  <div className="text-right font-semibold min-w-[90px]" style={{ color: bal >= 0 ? "hsl(var(--success))" : "hsl(var(--error))" }}>
                    {bal >= 0 ? "+" : ""}{fmtTRY(bal)}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* === BÖLÜM 4 (EN ALT): FULL SETTLEMENT — Kim Kime Ne Ödemeli === */}
      <div>
        <h2 className="text-lg font-display font-semibold mb-2 flex items-center gap-2">
          <GitCompare className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} /> 4. Kim Kime Ne Ödemeli (Kabin → Kabin)
        </h2>
        <div className="tk-card p-4 space-y-3" data-testid="full-settlement-card">
          {/* Transfer önerileri — Section 2 mini bloğuyla aynı sade format */}
          {fullSettlement.transfers.length === 0 ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: "hsl(var(--success))" }}>
              <CheckCircle2 className="w-4 h-4" /> Tüm dengeler sağlanmış — transfer gerekmiyor.
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="full-settlement-transfers">
              {fullSettlement.transfers.map((t, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm p-2.5 rounded-lg" style={{ backgroundColor: "hsl(var(--surface-2))" }} data-testid={`full-transfer-${idx}`}>
                  <span className="font-medium">{t.from_name}</span>
                  <ArrowRight className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                  <span className="font-medium">{t.to_name}</span>
                  <span className="ml-auto font-display font-semibold" style={{ color: "hsl(var(--primary))" }}>{fmtTRY(t.amount_try)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
